import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { handleUnauthorized } from "../../utils/apiHelper";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DangerAlert from "../child/Alert/DengerAlert";
import Loader from "../child/Loder";
import Multiselect from "multiselect-react-dropdown";

const SendNotificationNew = () => {
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [selectUserRole, setSelectUserRole] = useState("user"); // 👈 NEW: user or host
  const [selectType, setSelectType] = useState("all");
  const [selectUsers, setSelectUsers] = useState([]);
  const [users, setUsersData] = useState([]);
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const authToken = localStorage.getItem("authToken");

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/admin/userListnotification?role=${selectUserRole}`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${authToken}` },
      }
      );

      if (response.status === 401) {
        handleUnauthorized(navigate);
        return;
      }

      const data = await response.json();

      // ✅ Assume API returns userType (either 'user' or 'host')
      const allUsers = data.users || [];

      // Filter users by role type
      const filteredUsers = allUsers.filter(
        (u) => u.currentRole === selectUserRole
      );

      if (selectType === "all") {
        const allUserIds = filteredUsers.map((user) => user._id);
        setSelectUsers(allUserIds);
      }

      setUsersData(filteredUsers);
    } catch (error) {
      console.error(error);
      setErrorMessage("Failed to fetch user list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, [selectUserRole, selectType]); // 👈 Refetch when userType or type changes

  const clearMessage = () => {
    setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 3000);
  };

  const validateInputs = () => {
    const newErrors = {};
    if (!title) {
      newErrors.title = "Title is required";
    } else {
      if (!/[A-Za-z]/.test(title)) {
        newErrors.title = "Title must include at least one letter";
      }
      if (title.length < 3) {
        newErrors.title = "Title must be at least 3 characters long";
      }
    }
    if (selectType === "SelectedUser" && selectUsers.length === 0) {
      newErrors.selectUsers = "Please select at least one user";
    }
    if (!description) {
      newErrors.description = "Description is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateInputs()) return;

    let userId;
    if (selectType !== "all") {
      userId = selectUsers.map((user) => user.id);
    } else {
      userId = selectUsers;
    }

    setLoading(true);
    try {
      const method = isEditMode ? "PUT" : "POST";
      const API_ENDPOINT = `${process.env.REACT_APP_BASE_URL}/admin/sendNotificationAdmin`;

      const response = await fetch(API_ENDPOINT, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          title: title.trim(),
          notificationType: "admin_message",
          userType: selectUserRole, // 👈 Include role type
          userIds: userId,
          message: description,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setSuccessMessage("Notification sent successfully!");
        clearMessage();
        setTimeout(() => navigate("/notification"), 3000);
      } else {
        setErrorMessage(data.message || "Something went wrong");
        clearMessage();
      }
    } catch (error) {
      setErrorMessage(error?.message || "Something went wrong");
      clearMessage();
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="row gy-4">
      <div className="col-lg-12">
        <div className="card">
          <div className="card-body">
            {successMessage && <SuccessAlert message={successMessage} />}
            {errorMessage && <DangerAlert message={errorMessage} />}

            <form className="row gy-3" onSubmit={handleSubmit} noValidate>
              {/* Title */}
              <div className="col-md-12">
                <label className="form-label">
                  Title <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.title ? "is-invalid" : ""}`}
                  value={title}
                  placeholder="Enter Title"
                  onChange={(e) => {
                    setTitle(e.target.value.trimStart());
                    setErrors((prev) => ({ ...prev, title: "" }));
                  }}
                />
                {errors.title && (
                  <p className="text-danger mt-1">{errors.title}</p>
                )}
              </div>

              {/* Select Role (User / Host) */}
              <div className="col-md-12">
                <label className="form-label">
                  Select Type <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  value={selectUserRole}
                  onChange={(e) => setSelectUserRole(e.target.value)}
                >
                  <option value="user">User</option>
                  <option value="host">Host</option>
                </select>
              </div>

              {/* Select User Filter */}
              <div className="col-md-12 type">
                <label className="form-label">
                  Send To <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-control ${
                    errors.selectType ? "is-invalid" : ""
                  }`}
                  value={selectType}
                  onChange={(e) => {
                    if (e.target.value !== "all") setSelectUsers([]);
                    setSelectType(e.target.value);
                    setErrors((prev) => ({ ...prev, selectType: "" }));
                  }}
                >
                  <option value="all">All</option>
                  <option value="SelectedUser">Selected User</option>
                </select>
              </div>

              {/* Select User (Multiselect) */}
              {selectType === "SelectedUser" && (
                <div className="col-md-12">
                  <label className="form-label">
                    Select {selectUserRole === "user" ? "Users" : "Hosts"}{" "}
                    <span className="text-danger">*</span>
                  </label>

                  <Multiselect
                    options={users.map((u) => ({
                      name: u.fullName || "Unnamed",
                      id: u._id,
                    }))}
                    selectedValues={selectUsers}
                    onSelect={(selectedList) => {
                      setSelectUsers(selectedList);
                      setErrors((prev) => ({ ...prev, selectUsers: "" }));
                    }}
                    onRemove={(selectedList) => {
                      setSelectUsers(selectedList);
                      if (selectedList.length === 0) {
                        setErrors((prev) => ({
                          ...prev,
                          selectUsers: "Please select at least one user",
                        }));
                      }
                    }}
                    displayValue="name"
                    placeholder="Select Users"
                  />

                  {errors.selectUsers && (
                    <p className="text-danger mt-1">{errors.selectUsers}</p>
                  )}
                </div>
              )}

              {/* Description */}
              <div className="col-md-12">
                <label className="form-label">
                  Description <span className="text-danger">*</span>
                </label>
                <textarea
                  className={`form-control ${
                    errors.description ? "is-invalid" : ""
                  }`}
                  rows="5"
                  value={description}
                  placeholder="Enter Description"
                  onChange={(e) => {
                    setDescription(e.target.value.trimStart());
                    setErrors((prev) => ({ ...prev, description: "" }));
                  }}
                />
                {errors.description && (
                  <p className="text-danger mt-1">{errors.description}</p>
                )}
              </div>

              {/* Submit */}
              <div className="col-12">
                <button
                  className="btn btn-primary mt-3 float-end"
                  type="submit"
                >
                  {loading ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendNotificationNew;
